import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronRight, Phone, MessageCircle, MapPin, Star, CheckCircle, Loader2 } from "lucide-react";
import CarCard from "../components/CarCard";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

export const SupplierPage = () => {
  const { id } = useParams();
  const { t, tf } = useLanguage();
  const [supplier, setSupplier] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const supData = await odooApi.getSupplier(id);
        setSupplier(supData);
        const supCars = await odooApi.getSupplierCars(supData.id);
        setCars(supCars);
      } catch (err) {
        console.error("Failed to load supplier:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t("supplierPage.notFound", "Supplier Not Found")}</h2>
          <Link to="/cars" className="text-[#D4AF37] hover:underline">{t("carDetail.browseAll", "Browse all cars")}</Link>
        </div>
      </div>
    );
  }

  const whatsappUrl = `https://wa.me/${(supplier.whatsapp || supplier.phone || '').replace(/[^0-9]/g, "")}?text=${encodeURIComponent(tf("supplierPage.whatsappPrefill", { supplier: supplier.name }, `Hi! I'm interested in renting a car from ${supplier.name}.`))}`;
  const handleContactClick = (type) => {
    odooApi.trackClick({
      click_type: type,
      supplier_id: supplier.id,
    });
  };

  const features = [
    t("carDetail.delivery", "Free Delivery & Pickup"),
    t("supplierPage.feature.airportTransfer", "Airport Transfer"),
    t("carDetail.roadside", "24/7 Roadside Assistance"),
    t("carDetail.insurance", "Comprehensive Insurance"),
    t("supplierPage.feature.flexiblePayment", "Flexible Payment Options"),
    t("supplierPage.feature.multiLanguageSupport", "Multi-language Support"),
  ];

  return (
    <>
      <SEO
        title={`${supplier.name} | Car Rental Supplier in ${supplier.city || 'UAE'}`}
        description={`Rent cars from ${supplier.name} in ${supplier.city || 'UAE'}. ${supplier.rating} star rating, ${supplier.reviewCount}+ reviews. Premium fleet available at competitive prices.`}
        keywords={`${supplier.name} car rental, ${supplier.city || 'dubai'} car hire, car rental supplier uae`}
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("common.suppliers", "Suppliers"), url: "/cars" },
          { name: supplier.name },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="supplier-page">
        <div className="bg-black py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-white/60 hover:text-[#D4AF37]">{t("nav.home", "Home")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <Link to="/cars" className="text-white/60 hover:text-[#D4AF37]">{t("common.suppliers", "Suppliers")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-white">{supplier.name}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-start gap-6">
                  {supplier.logo ? (
                    <img src={supplier.logo} alt={supplier.name} className="w-24 h-24 rounded-lg object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                      <span className="text-[#D4AF37] font-bold text-2xl">{supplier.name?.substring(0, 2)}</span>
                    </div>
                  )}
                  <div>
                    <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-white mb-2">
                      {supplier.name}
                    </h1>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
                        <span className="text-white font-semibold">{supplier.rating || 'N/A'}</span>
                        {supplier.reviewCount > 0 && <span className="text-white/60">{tf("supplierPage.reviewCount", { count: supplier.reviewCount }, `(${supplier.reviewCount} reviews)`)}</span>}
                      </div>
                      {supplier.isVerified && <span className="text-[#D4AF37]">{t("common.verified", "Verified")}</span>}
                    </div>
                    <p className="text-white/70 max-w-xl">{supplier.description || tf("supplierPage.descriptionFallback", { city: supplier.city || t("common.uae", "UAE") }, `Premium car rental service in ${supplier.city || 'UAE'}.`)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 w-full md:w-80">
                <h3 className="font-semibold text-white mb-4">{t("common.contactSupplier", "Contact Supplier")}</h3>
                <div className="space-y-4 mb-6">
                  {supplier.phone && (
                    <div className="flex items-center gap-3 text-white/80">
                      <Phone className="w-4 h-4 text-[#D4AF37]" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                  {supplier.city && (
                    <div className="flex items-center gap-3 text-white/80">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-sm">{supplier.city}, {t("common.uae", "UAE")}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => handleContactClick('whatsapp')} className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white py-3 rounded-sm hover:bg-[#1da851] transition-colors font-semibold" data-testid="supplier-whatsapp">
                    <MessageCircle className="w-5 h-5" />{t("common.whatsapp", "WhatsApp")}
                  </a>
                  {supplier.phone && (
                    <a href={`tel:${supplier.phone}`} onClick={() => handleContactClick('call')} className="flex items-center justify-center gap-2 w-full bg-white text-black py-3 rounded-sm hover:bg-gray-100 transition-colors font-semibold" data-testid="supplier-call">
                      <Phone className="w-5 h-5" />{t("btn.callNow", "Call Now")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 border-y dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
              {tf("supplierPage.carsFrom", { supplier: supplier.name }, `Cars from ${supplier.name}`)}
            </h2>
            <Link to="/cars" className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center gap-1">
              {t("btn.viewAll", "View All")}<ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {cars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="supplier-cars-grid">
              {cars.map((car) => (<CarCard key={car.id} car={car} />))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <p className="text-lg">{t("supplierPage.empty", "No cars listed by this supplier at the moment.")}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SupplierPage;

